import { Navigate, useNavigate } from "react-router";
import { useFocusedNavigation } from "../../../hooks/useFocusedNavigation";
import { CheckoutPageLayout } from "./lib/CheckoutPageLayout";
import { CheckoutOrderSummary } from "./lib/CheckoutOrderSummary";
import { useCheckoutOrderSummary } from "./lib/useCheckoutOrderSummary";
import { CheckoutProvider } from "./lib/CheckoutProvider";
import { useCheckout } from "./lib/CheckoutProvider.shared";
import { EmptySupplierQueryParamBoundary } from "./lib/EmptySupplierQueryParamBoundary";
import { EmptyBusinessBoundary } from "./lib/EmptyBusinessBoundary";
import { EmptySupplierBoundary } from "./lib/EmptySupplierBoundary";
import { useBasketContext } from "../../../hooks";
import { mapBasketOrderToCheckoutOrderItems } from "./lib/mapBasketOrderToCheckoutOrderItems";
import { useStorefrontPromotionRewardItemSelectorParameters } from "../categoryPage/Storefront/StorefrontPromotion/StorefrontPromotionRewardItemSelector";
import {
  CHECKOUT_EDIT_ORDER_BUYER,
  CHECKOUT_BUYER,
  ORDER_BUYER,
} from "../../../constants/routes";
import { getParentIdsForBadgesFromOrder } from "../categoryPage/CategoryBadgesDialog/CategoryBadgesDialog.shared";
import { useStockList } from "../../../hooks/api/v2/stockList/useStockList";
import { CheckoutPageLoading } from "./lib/CheckoutPageLoading";
import { StockListResponseType } from "@sellar-io/api-v2/types";
import { ItemQuantity } from "../categoryPage/ItemQuantity";
import { formatCurrency, pluralize } from "@sellar-io/common";
import {
  Button,
  Icon,
  ImageWithFallback,
  ProfilePicture,
  SmallButton,
} from "@sellar-io/components";
import { Fragment, ReactNode, useState } from "react";
import { getCanPromotionRewardItemBeClaimed } from "../categoryPage/Storefront/StorefrontPromotion/getCanPromotionRewardItemBeClaimed";
import { Alert } from "../../../components/alert/Alert";

export const CheckoutV2AddOnPage = () => {
  useFocusedNavigation(); // Hides all drawers - sidebar and basket drawer
  return (
    <CheckoutProvider>
      <EmptySupplierQueryParamBoundary>
        <EmptyBusinessBoundary>
          <EmptySupplierBoundary>
            <CheckoutV2AddOnLayout />
          </EmptySupplierBoundary>
        </EmptyBusinessBoundary>
      </EmptySupplierQueryParamBoundary>
    </CheckoutProvider>
  );
};

// Display mode order = keg-badges -> promotions -> main checkout
type Mode = "keg-badges" | "promotions";

const CheckoutV2AddOnLayout = () => {
  const navigate = useNavigate();
  const [{ order }] = useBasketContext();
  const [mode, setMode] = useState<Mode | undefined>(undefined);
  const [
    newlyAddedPromotionRewardItemIds,
    setNewlyAddedPromotionRewardItemIds,
  ] = useState<number[]>([]);

  const {
    isLoading: isCheckoutLoading,
    storefront,
    supplierUrlIdInParam,
    supplier,
    priceTierLink: storefrontLink,
  } = useCheckout();

  const supplierId = supplier?.id ?? 0;
  const supplierName = supplier?.Business.name ?? "";

  const storefrontPromotionRewardItemSelectorParameters =
    useStorefrontPromotionRewardItemSelectorParameters();

  const orderSummary = useCheckoutOrderSummary({
    supplierStorefront: storefront,
    order,
  });

  const specificParentIds = getParentIdsForBadgesFromOrder(order) ?? [];

  const badgeProductsListQuery = useStockList(
    {
      ParentId: null,
      active: true,
      SupplierId: supplierId,
      isPreview: false,
      storefrontLinkPassword: storefrontLink ?? undefined,
      productTypeOnly: "badge",
      specificParentIds,
    },
    {
      enabled: specificParentIds.length > 0,
    },
  );

  const goToSupplierStorefront = () => {
    navigate(ORDER_BUYER.href({ supplierIdOrUrlId: supplierUrlIdInParam }), {
      replace: true,
    });
  };

  const isPageLoading =
    isCheckoutLoading ||
    badgeProductsListQuery.isLoading ||
    storefrontPromotionRewardItemSelectorParameters.isFetchingPromotions;

  if (isPageLoading) {
    return (
      <CheckoutPageLoading description="Loading your order" title="Checkout" />
    );
  }

  const badgeProducts =
    badgeProductsListQuery.data?.pages.flatMap((page) => page) ?? [];

  const checkoutUrl = `${
    order?.id
      ? CHECKOUT_EDIT_ORDER_BUYER.href({
          supplierIdOrUrlId: supplierUrlIdInParam,
          orderId: order.id,
        })
      : CHECKOUT_BUYER.href({ supplierIdOrUrlId: supplierUrlIdInParam })
  }${location.search}`;

  const canShowPromotionsAddOn = computeCanShowPromotions({
    newlyAddedPromotionRewardItemIds,
    storefrontPromotionRewardItemSelectorParameters,
  });

  const canShowKegBadgesAddOn =
    Boolean(specificParentIds) &&
    specificParentIds.length > 0 &&
    badgeProducts.length > 0;

  //   Redirect to normal checkout if not eligible for:
  //   - Keg badges
  //   - Promotions
  if (!canShowKegBadgesAddOn && !canShowPromotionsAddOn) {
    return <Navigate to={checkoutUrl} replace />;
  }

  const checkoutOrderItems = mapBasketOrderToCheckoutOrderItems(
    order?.OrderItems ?? [],
  );

  const promotions = storefrontPromotionRewardItemSelectorParameters.promotions;

  const currentMode =
    mode ?? getInitialMode({ canShowKegBadgesAddOn, canShowPromotionsAddOn });

  const navTitle = "Optional extras related to your order";

  return (
    <CheckoutPageLayout
      title={navTitle}
      onClickLeftIcon={goToSupplierStorefront}
      layout="centered"
      aside={
        <CheckoutOrderSummary
          orderSummary={orderSummary}
          orderItemList={checkoutOrderItems}
          outOfStockProductIds={[]}
          productIdsWithPricingChange={[]}
          activeProductIdList={[]}
          promotions={promotions}
        />
      }
      alerts={[]}
    >
      {currentMode === "keg-badges" && (
        <KegBadgesAddOn
          badgeProducts={badgeProducts}
          supplierName={supplierName}
          onConfirm={() => {
            if (!canShowPromotionsAddOn) {
              navigate(checkoutUrl);
              return;
            }
            setMode("promotions");
          }}
        />
      )}
      {currentMode === "promotions" && (
        <PromotionsAddOn
          storefrontPromotionSelectorParameters={
            storefrontPromotionRewardItemSelectorParameters
          }
          onConfirm={() => {
            navigate(checkoutUrl, { replace: true });
          }}
          onClaimPromotionRewardItem={(id) => {
            setNewlyAddedPromotionRewardItemIds((prev) => [...prev, id]);
          }}
        />
      )}
    </CheckoutPageLayout>
  );
};

type KegBadgesAddOnProps = {
  supplierName: string;
  badgeProducts: StockListResponseType;
  onConfirm: () => void;
};

const KegBadgesAddOn = ({
  supplierName,
  badgeProducts,
  onConfirm,
}: KegBadgesAddOnProps) => {
  const badges = badgeProducts.flatMap((badgeProduct) => {
    return badgeProduct.Products.map((badge) => {
      return {
        ...badge,
        Product: {
          id: badgeProduct.id,
          imageUrl: badgeProduct.imageUrl,
          name: badgeProduct.name,
        },
      };
    });
  });

  return (
    <AddOnPanel
      title="Keg badges"
      subtitle={` Let ${supplierName} know how many keg badges to send with the draft
              products in your order`}
      footer={
        <Button onClick={onConfirm} className="w-full">
          Next
        </Button>
      }
    >
      <div className="flex flex-col gap-2">
        {badges.map((badge) => {
          console.log(badge.name, badge.Product.name);
          const productNameParts = badge.name?.split(" • ");
          const badgeName = productNameParts?.[0] ?? "";
          const badgeType = productNameParts?.[1] ?? "";
          const badgeShape = productNameParts?.[2] ?? "";

          const badgeSegments = [badgeName, badgeType, badgeShape].filter(
            Boolean,
          );

          return (
            <div
              key={badge.id}
              className="flex flex-col justify-between gap-2 rounded-lg bg-white px-4 py-3 sm:flex-row sm:gap-0"
            >
              <div className="flex items-center gap-2">
                <ProfilePicture
                  logoUrl={badge.Product.imageUrl ?? null}
                  fallback="product-badge"
                  size="small"
                />
                <span>
                  {badgeSegments.map((segment, index) => (
                    <Fragment key={`${segment}-${index}`}>
                      {index === 0 ? (
                        <span className="font-semibold">{segment}</span>
                      ) : (
                        <span>{segment}</span>
                      )}
                      {index < badgeSegments.length - 1 && " • "}
                    </Fragment>
                  ))}
                </span>
              </div>
              <div className="flex w-full items-center justify-between gap-4 sm:w-fit">
                <span className="font-semibold">
                  {formatCurrency(badge.ProductPriceTier?.fullPrice ?? 0)}
                </span>
                <ItemQuantity productItem={badge} />
              </div>
            </div>
          );
        })}
      </div>
    </AddOnPanel>
  );
};

type PromotionsAddOnProps = {
  onConfirm: () => void;
  storefrontPromotionSelectorParameters: ReturnType<
    typeof useStorefrontPromotionRewardItemSelectorParameters
  >;
  onClaimPromotionRewardItem: (id: number) => void;
};

const PromotionsAddOn = ({
  storefrontPromotionSelectorParameters,
  onConfirm,
  onClaimPromotionRewardItem,
}: PromotionsAddOnProps) => {
  const [errorMessage, setErrorMessage] = useState<string | null>("");
  const {
    promotions,
    rewardsWithSpendThresholdMet,
    claimedPromotionRewardItemIds,
    nonPromotionBasketOrderItems,
    unclaimPromotionRewardItem,
    claimPromotionRewardItem,
    handleApplyPromotionsToBasket,
  } = storefrontPromotionSelectorParameters;

  const handleClaimPromotionRewardItem = ({
    newPromotionRewardItemId,
    spendThreshold,
  }: {
    newPromotionRewardItemId: number;
    spendThreshold: number;
  }) => {
    setErrorMessage(null);
    const { canBeClaimed, message } = getCanPromotionRewardItemBeClaimed({
      promotionRewardItemId: newPromotionRewardItemId,
      nonPromotionBasketOrderItems,
      claimedPromotionRewardItemIds,
      promotions,
    });

    if (!canBeClaimed) {
      let newErrorMessage = "Unable to claim this reward";
      switch (message) {
        case "REWARD_RULE_MET": {
          newErrorMessage = "Only 1 reward can be added from this promotion";
          break;
        }
        case "SPEND_THRSHOLD_NOT_MET": {
          newErrorMessage = `You need to spend up to ${formatCurrency(spendThreshold)} to claim this reward`;
          break;
        }
        case "MISSING_PROMOTION":
        case "MISSING_PROMOTION_REWARD_ITEM":
        case "PROMOTION_REWARD_ITEM_ALREADY_CLAIMED":
          newErrorMessage = `This reward has already been claimed`;
          break;
        case "UNKNOWN_ERROR":
      }

      setErrorMessage(newErrorMessage);
      return;
    }

    claimPromotionRewardItem({
      promotionRewardItemIds: [
        ...claimedPromotionRewardItemIds,
        newPromotionRewardItemId,
      ],
      onComplete: handleApplyPromotionsToBasket,
    });

    onClaimPromotionRewardItem(newPromotionRewardItemId);
  };

  return (
    <AddOnPanel
      title="Promotions"
      subtitle={`You're eligible for ${rewardsWithSpendThresholdMet.length} ${pluralize("reward", rewardsWithSpendThresholdMet.length)}, add them to your basket here`}
      footer={
        <Button onClick={onConfirm} className="w-full">
          Next
        </Button>
      }
    >
      {errorMessage && (
        <Alert type="error" className="w-full" message={errorMessage} />
      )}
      <div className="flex flex-col gap-4">
        {promotions.map((promotion) => {
          return (
            <div key={promotion.id} className="flex flex-col gap-4">
              <div className="flex flex-col">
                <div className="flex items-center gap-2">
                  <p className="font-semibold">{promotion.name}</p>
                  <div className="flex items-center gap-2 rounded-full bg-purple-100 px-2 py-1 text-sm text-purple-800">
                    <Icon icon="PARTY_POPPER" size={0.7} />
                    <span>Promotion</span>
                  </div>
                </div>
                {promotion.rewardRule === "one_of" && (
                  <p>1 reward can be claimed</p>
                )}
                {promotion.rewardRule === "all_of" && (
                  <p>All unlocked rewards can be claimed</p>
                )}
              </div>
              <div className="flex flex-col gap-2">
                {promotion.PromotionRewardItems.map((promotionRewardItem) => {
                  const rewardName =
                    promotionRewardItem.Product.nameV2 ||
                    promotionRewardItem.Product.name;
                  const rewardPrice =
                    promotionRewardItem.price === 0
                      ? "free"
                      : formatCurrency(promotionRewardItem.price);
                  const rewardImage =
                    promotionRewardItem.Product.imageUrl ||
                    promotionRewardItem.Product.Parent?.imageUrl;

                  const rewardQuantity =
                    promotionRewardItem.quantity === 1
                      ? "a"
                      : `${promotionRewardItem.quantity} x`;

                  const hasPromotionRewardItemBeenClaimed =
                    claimedPromotionRewardItemIds.includes(
                      promotionRewardItem.id,
                    );
                  return (
                    <div
                      key={promotionRewardItem.id}
                      className="flex flex-col items-center justify-between gap-2 rounded-lg bg-white px-4 py-3 sm:flex-row"
                    >
                      <div className="flex w-full items-center gap-2 sm:w-3/4">
                        <ImageWithFallback
                          img={rewardImage}
                          className="h-9 w-9 rounded-full"
                          fallback="product-badge"
                        />
                        <p>
                          Spend{" "}
                          <strong>
                            {formatCurrency(promotionRewardItem.spendThreshold)}
                          </strong>{" "}
                          and receive{" "}
                          <strong>
                            {rewardQuantity} {rewardName}
                          </strong>{" "}
                          for <strong>{rewardPrice}</strong>
                        </p>
                      </div>
                      {hasPromotionRewardItemBeenClaimed ? (
                        <SmallButton
                          className="w-full bg-zinc-200 sm:w-fit"
                          onClick={() => {
                            unclaimPromotionRewardItem({
                              promotionRewardItemIds: [promotionRewardItem.id],
                              onComplete: handleApplyPromotionsToBasket,
                            });
                          }}
                          color="slim"
                        >
                          Remove
                        </SmallButton>
                      ) : (
                        <SmallButton
                          color="slim"
                          className="w-full sm:w-fit"
                          onClick={() => {
                            handleClaimPromotionRewardItem({
                              newPromotionRewardItemId: promotionRewardItem.id,
                              spendThreshold:
                                promotionRewardItem.spendThreshold,
                            });
                          }}
                        >
                          Add to Basket
                        </SmallButton>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </AddOnPanel>
  );
};

const AddOnPanel = ({
  title,
  subtitle,
  children,
  footer,
}: {
  title: string;
  subtitle: string;
  children: ReactNode;
  footer: ReactNode;
}) => {
  return (
    <div className="flex h-full flex-1 flex-col gap-4">
      <div className="flex flex-1 flex-col rounded-lg">
        <div className="mb-4 flex flex-col gap-4 rounded-lg bg-zinc-100 p-4 text-zinc-800">
          <div className="flex flex-col gap-1">
            <p className="text-lg font-semibold">{title}</p>
            <p className="text-zinc-500">{subtitle}</p>
          </div>
          {children}
        </div>
      </div>
      <div className="sticky bottom-4 w-full px-4 sm:bottom-0 sm:px-0">
        {footer}
      </div>
    </div>
  );
};

const getInitialMode = ({
  canShowKegBadgesAddOn,
  canShowPromotionsAddOn,
}: {
  canShowPromotionsAddOn: boolean;
  canShowKegBadgesAddOn: boolean;
}): Mode | undefined => {
  if (canShowKegBadgesAddOn) {
    return "keg-badges";
  }

  if (canShowPromotionsAddOn) {
    return "promotions";
  }

  return undefined;
};

const computeCanShowPromotions = ({
  newlyAddedPromotionRewardItemIds,
  storefrontPromotionRewardItemSelectorParameters,
}: {
  newlyAddedPromotionRewardItemIds: number[];
  storefrontPromotionRewardItemSelectorParameters: ReturnType<
    typeof useStorefrontPromotionRewardItemSelectorParameters
  >;
}): boolean => {
  const {
    promotions,
    promotionsWithRewardsWhereSpendThresholdMet,
    hasEligibleRewardsAvailable,
  } = storefrontPromotionRewardItemSelectorParameters;

  if (promotions.length === 0) {
    return false;
  }

  if (promotionsWithRewardsWhereSpendThresholdMet.length === 0) {
    return false;
  }

  if (
    !hasEligibleRewardsAvailable &&
    newlyAddedPromotionRewardItemIds.length <= 0
  ) {
    return false;
  }

  return true;
};
